import { useEffect } from 'react';
import { canonicalUrl, type SeoMeta } from '../lib/seo';

interface Props extends SeoMeta {
  /**
   * Path canónico explícito (p. ej. `/blog/<slug>`). Si se omite, se usa el
   * `pathname` actual de `window.location`. Útil cuando el canonical no coincide
   * con la ubicación (rutas con parámetros).
   */
  canonicalPath?: string;
}

/**
 * Gestión del `<head>` en cliente para esta SPA (un único `index.html` estático
 * para toda ruta). Al montarse o cambiar de ruta, actualiza imperativamente
 * sobre `document.head`:
 *
 * - `<link rel="canonical">` autorreferente y absoluto sobre `SITE_URL`.
 * - `<meta name="robots">` con la directiva de indexación de la ruta.
 * - `<title>` y `<meta name="description">` por ruta.
 *
 * Siempre reutiliza el elemento existente (lo busca antes de crearlo), así que
 * no se acumulan canonicals ni metas duplicados al navegar. No renderiza nada.
 *
 * Lee `window.location.pathname` en vez de `useLocation()` a propósito: el
 * `<head>` es un concern del DOM, no del router, y bajo `BrowserRouter` la URL
 * ya está actualizada cuando la página de la nueva ruta (y su `<Seo>`) monta.
 * Así el componente no exige contexto de router para montarse.
 */
export default function Seo({ title, description, robots, canonicalPath }: Props) {
  const path = canonicalPath ?? window.location.pathname;

  useEffect(() => {
    document.title = title;
    upsertMetaName('description', description);
    upsertMetaName('robots', robots);
    upsertCanonical(canonicalUrl(path));
  }, [title, description, robots, path]);

  return null;
}

/** Crea o actualiza el único `<meta name="...">` del `<head>`. */
function upsertMetaName(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Crea o actualiza el único `<link rel="canonical">` del `<head>`. */
function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
